import { Check, Copy } from 'lucide-react'
import type { ReactNode } from 'react'
import './copyable.css'
import { Tooltip } from './Tooltip'
import { useCopy } from './useCopy'

/**
 * A value shown so it can be taken — click anywhere on it to copy.
 *
 * One component, because a value worth displaying exactly is a value somebody
 * is about to paste somewhere: a key quoted in a commit, a token dropped into a
 * template, a link handed to somebody else. Written per screen it becomes a
 * different affordance each time — some with a button, some with a tooltip,
 * some silent about whether the copy happened — and a reader learns whichever
 * they met first.
 *
 * The whole value is the target rather than an icon beside it. The icon says
 * what the control does; it is not the part you have to hit.
 *
 * It confirms, briefly, in place. A copy that says nothing leaves somebody
 * pasting to find out whether it worked, and a toast for something this small
 * is a notification about a click.
 */
export function Copyable({
  value,
  what = 'value',
  children,
}: {
  /** What lands on the clipboard. */
  value: string
  /** Names it in the tooltip — "Copy key", "Copy link". */
  what?: string
  /** What is shown, where that differs from what is copied. */
  children?: ReactNode
}) {
  const [copied, copy] = useCopy()

  return (
    <Tooltip label={copied ? 'Copied' : `Copy ${what}`}>
      <button
        type="button"
        className="copyable"
        data-copied={copied ? '' : undefined}
        onClick={() => copy(value)}
      >
        <span className="copyable__value">{children ?? value}</span>
        {copied ? (
          <Check size={14} aria-hidden />
        ) : (
          <Copy size={14} aria-hidden />
        )}
      </button>
    </Tooltip>
  )
}
